import React, { useState, useEffect } from "react";
import {
  KFDataTable,
  KFDataTableColDef,
  KFDropdown,
  KFTextInput
} from "@kf/ic2fx";
import MenuItem from "@mui/material/MenuItem";
// @ts-ignore
import Chart from "react-apexcharts";
// @ts-ignore
import { ApexOptions } from "apexcharts";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/reducers";
// @ts-ignore
import Legend from "../Legend";
import { reportDataThunk } from "../../../store/slices/reportsSlice";
import Loader from "../../loader";

import { PageView, ReportTypes } from "../../../constants/app.constant";
import styles from "./PayEquityByJobLevel.module.scss";

const PayEquityByJobLevel = ({ pageView = PageView.CHART }: any) => {
  const [jobLevelReportData, setJobLevelReportData] = useState<any>({});
  const [dispalyBy, setDisplayBy] = useState<any>("MEDIAN");
  const [barChartOptions, setBarChartOptions] = useState<ApexOptions>({
    series: [
      {
        name: "Female",
        data: [],
        color: "#FFC759"
      },
      {
        name: "Male",
        data: [],
        color: "#00C29B"
      }
    ],
    chart: {
      type: "bar",
      toolbar: {
        show: false
      }
    },
    plotOptions: {
      bar: {
        borderRadius: 10,
        horizontal: false
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"]
    },
    xaxis: {
      title: {
        text: "Job Grade",
        style: {
          fontSize: "16px"
        }
      },
      categories: []
    },
    yaxis: {
      title: {
        text: "Median Pay",
        style: {
          fontSize: "16px"
        }
      }
    },
    legend: {
      show: false,
      position: "bottom",
      fontSize: "16px",
      fontWeight: 400,
      markers: {
        size: 16, // to be checked
        // width: 16,
        // height: 16,
        // radius: 5
      },
      itemMargin: {
        horizontal: 10
      },
      height: 50
    },
    fill: {
      opacity: 1
    },
    tooltip: {
      y: {
        formatter: function (val: any) {
          return "$ " + val + " thousands";
        }
      }
    }
  });

  const dispatch = useDispatch();

  const legendData = [
    {
      name: (
        <>
          Female <strong>100%</strong>
        </>
      ),
      color: "#FFC759"
    },
    {
      name: (
        <>
          Male <strong>106%</strong>
        </>
      ),
      color: "#00C29B"
    }
  ];

  const columnDefs: KFDataTableColDef[] = [
    {
      field: "gradeName",
      headerName: "Grade",
      flex: 1
    },
    {
      field: "female",
      headerName: "Female",
      flex: 1
    },
    {
      field: "male",
      headerName: "Male",
      flex: 1
    },
    {
      field: "gap",
      headerName: "Wage Gap",
      flex: 1
    }
  ];

  const { globalFilters } = useSelector((state: RootState) => {
    return state.reports;
  });

  const { isReportsLoading, reportsData } = useSelector((state: RootState) => {
    return state.reports;
  });

  const initReportData = () => {
    const _globalFilters = { ...globalFilters };

    delete _globalFilters.hayPointsMin;
    delete _globalFilters.hayPointsMax;

    const payload = {
      type: ReportTypes.PAY_EQUITY_BY_JOB_LEVEL,
      filters: _globalFilters
    };

    dispatch(reportDataThunk(payload));
  };

  const applyDisplayFilter = (display: string) => {
    const data = reportsData[ReportTypes.PAY_EQUITY_BY_JOB_LEVEL] || [];
    let reportData = [];
    const femaleDataList: any[] = [];
    const maleDataList: any[] = [];
    const gradesList: any[] = [];

    reportData = data.map((row: any, index: number) => {
      let male = row.gradeMedianMale;
      let female = row.gradeMedianFemale;

      if (display === "HEADCOUNT") {
        male = row.gradeCountMale;
        female = row.gradeCountFemale;
      }
      if (display === "HEADCOUNT_PERCENTAGE") {
        male = row.gradeAVGMale;
        female = row.gradeAVGFemale;
      }
      maleDataList.push(male);
      femaleDataList.push(female);
      gradesList.push(row.gradeName);

      return {
        ...row,
        id: index + 1,
        male: male,
        female: female,
        gap: male > female ? male - female : female - male
      };
    });

    const _barChartOptions: ApexOptions = { ...barChartOptions };
    _barChartOptions.series = [
      {
        name: "Female",
        data: femaleDataList,
        color: "#FFC759"
      },
      {
        name: "Male",
        data: maleDataList,
        color: "#00C29B"
      }
    ];

    _barChartOptions.xaxis = {
      ...barChartOptions.xaxis,
      categories: gradesList
    };

    _barChartOptions.yaxis = {
      title: {
        text: display,
        style: {
          fontSize: "16px"
        }
      }
    };

    setBarChartOptions(_barChartOptions);
    setJobLevelReportData(reportData);
  };

  useEffect(() => {
    applyDisplayFilter("MEDIAN");
  }, [reportsData]);

  useEffect(() => {
    if (globalFilters) {
      initReportData();
    }
  }, [globalFilters]);

  return (
    <div className={styles.reportContainer}>
      {isReportsLoading && <Loader />}

      <div className="reportFilter">
        <KFDropdown
          label="Display:"
          onChange={(e: any) => {
            setDisplayBy(e.target.value);
            applyDisplayFilter(e.target.value);
          }}
          value={dispalyBy}
        >
          <MenuItem disableRipple value={"MEDIAN"}>
            Median pay
          </MenuItem>
          <MenuItem disableRipple value={"HEADCOUNT"}>
            Headcount
          </MenuItem>
          <MenuItem disableRipple value={"HEADCOUNT_PERCENTAGE"}>
            Headcount %
          </MenuItem>
        </KFDropdown>

        <KFDropdown label="Grade:" onChange={() => {}} value="">
          <MenuItem disableRipple value={""}>
            Default job grade
          </MenuItem>
        </KFDropdown>

        <KFTextInput
          label="Wage gap threshold:"
          placeholder="Add a value"
          autoComplete="off"
        />
      </div>
      {pageView === "LIST" && (
        <div className={styles.listContainer}>
          <KFDataTable
            columns={columnDefs}
            rows={jobLevelReportData}
            hideFooter
          />
        </div>
      )}
      {pageView === "CHART" && (
        <div className={styles.chartContainer}>
          <div className={styles.chartWrappers}>
            <div className={styles.chartWrapper}>
              <Chart
                options={barChartOptions}
                series={barChartOptions.series}
                type="bar"
                height={"100%"}
              />
            </div>
            <div className={styles.chartInsightWrapper}>
              <div className={styles.chartInsight}>
                <div className={styles.insightTitle}>Insight</div>
                <div className={styles.chartDescription}>
                  This chart shows the average (mean) pay by gender and the
                  percentage difference between them.
                </div>
              </div>
              <Legend
                data={legendData}
                title="Legend"
                className="rightLegend"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayEquityByJobLevel;
